import { html, CSSResultGroup } from 'lit';
import { property } from 'lit/decorators.js';
import { ifDefined } from 'lit/directives/if-defined.js';
import { BlocksElement } from '../../BlocksElement';
import styles from '../icon.styles';
import type { IconColorType, IconSize } from './icon.model';

/**
 * CloseCircle component
 * @csspart base- The component's base wrapper
 */
export class BlocksIconCloseCircle extends BlocksElement {static styles: CSSResultGroup = styles;

  /**
   * Icon color variant
   * <cat-text-passage size="sm">
   * <ul>
   * <li>No color defined renders an icon with the currentColor of its parent element</li>
   * <li>**content-strong** renders an icon with the cat-theme-color-content-strong color token</li>
   * <li>**knockout** renders an icon with the cat-theme-color-content-knockout color token</li>
   * <li>**knockout-brand** renders a renders an icon with the cat-theme-color-content-knockout-brand color token</li>
   * <li>**information** renders an icon with the cat-theme-color-content-utility-information color token</li>
   * <li>**success** renders an icon with the cat-theme-color-content-utility-success color token</li>
   * <li>**warning** renders a renders an icon with the cat-theme-color-content-utility-warning color token</li>
   * <li>**error** renders an icon with the cat-theme-color-content-utility-error color token</li>
   * </ul>
   * </cat-text-passage>
   */
  @property({ type: String })
  color: IconColorType;

  /**
   * Title
   * 1) Icons should always have associated text. Use this to supply `aria-label` to icon if it stands by itself without any visual/visually-hidden text
   */
  @property({ type: String })
  iconTitle: string | undefined;

  /**
   * Size property
   * - Default size is 16px
   * - **sm** renders a larger size than default (12px)
   * - **lg** renders a larger size than default (24px)
   * - **xl** renders a larger size than the lg variant (32px);
   */
  @property({ type: String })
  size: IconSize;

  connectedCallback() {
    super.connectedCallback();
    this.classList.add('cat-icon');
  }

  render() {
    const componentClassName = this.componentClassNames('cat-c-icon', {
      'cat-c-icon--sm': this.size === 'sm',
      'cat-c-icon--lg': this.size === 'lg',
      'cat-c-icon--xl': this.size === 'xl',
      'cat-c-icon--content-strong': this.color === 'content-strong',
      'cat-c-icon--knockout': this.color === 'knockout',
      'cat-c-icon--knockout-brand': this.color === 'knockout-brand',
      'cat-c-icon--information': this.color === 'information',
      'cat-c-icon--success': this.color === 'success',
      'cat-c-icon--warning': this.color === 'warning',
      'cat-c-icon--error': this.color === 'error'
    });

    return html`
      <span part="base" class="${componentClassName}" role="presentation">
        <svg
          aria-hidden="${!this.iconTitle}"
          aria-label="${ifDefined(this.iconTitle)}"
          role="${this.iconTitle && 'img'}"
          width="24"
          height="24"
          viewBox="0 0 24 24"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            fill-rule="evenodd"
            clip-rule="evenodd"
            d="M2.60547 11.9995C2.60547 6.81078 6.81175 2.60449 12.0005 2.60449C17.1892 2.60449 21.3955 6.81078 21.3955 11.9995C21.3955 17.1882 17.1892 21.3945 12.0005 21.3945C6.81175 21.3945 2.60547 17.1882 2.60547 11.9995ZM12.0005 1.10449C5.98333 1.10449 1.10547 5.98235 1.10547 11.9995C1.10547 18.0166 5.98333 22.8945 12.0005 22.8945C18.0176 22.8945 22.8955 18.0166 22.8955 11.9995C22.8955 5.98235 18.0176 1.10449 12.0005 1.10449ZM15.8396 8.16108C16.1325 8.45397 16.1325 8.92884 15.8396 9.22174L13.0611 12.0002L15.8396 14.7787C16.1325 15.0716 16.1325 15.5465 15.8396 15.8394C15.5467 16.1323 15.0719 16.1323 14.779 15.8394L12.0005 13.0609L9.22198 15.8394C8.92909 16.1323 8.45421 16.1323 8.16132 15.8394C7.86843 15.5465 7.86843 15.0716 8.16132 14.7787L10.9398 12.0002L8.16132 9.22174C7.86843 8.92884 7.86843 8.45397 8.16132 8.16108C8.45421 7.86818 8.92909 7.86818 9.22198 8.16108L12.0005 10.9396L14.779 8.16108C15.0719 7.86818 15.5467 7.86818 15.8396 8.16108Z"
          />
        </svg>
      </span>
    `;
  }
}

if (customElements.get('cat-icon-close-circle') === undefined) {
  customElements.define('cat-icon-close-circle', BlocksIconCloseCircle);
}

declare global {
  interface HTMLElementTagNameMap {
    'cat-icon-close-circle': BlocksIconCloseCircle;
  }
}
